import type { NcfCoverage, SchoolClassRow, SchoolOverview } from '../../../shared/api/school'

/**
 * E1's and E5's arithmetic, with no React in it (OD-60 — the repo has no DOM
 * renderer and may not gain one, so branching inside a component is branching no
 * test can reach).
 *
 * 🔴 THE RULE THIS FILE EXISTS TO HOLD IS OD-39 AND OD-72: **no percentage
 * without a stated denominator.** Every function that returns a ratio returns
 * the two numbers it was made from as well, and nothing here formats a `%`. A
 * school reads a percentage as a grade; "12 of 40 children played this week" is
 * a fact they can act on.
 */

/* ───────────────────────────────────────────────── the headline pair ── */

export interface Fraction {
  count: number
  total: number
}

/**
 * ⚠️ A PAIR, NOT A RATIO. The type has no `percent` field on purpose: a field
 * that exists is a field a component eventually renders, and the copy in
 * `adminCopy.ts` takes both numbers so the sentence cannot be written without
 * its denominator.
 */
export function playedThisWeek(overview: SchoolOverview): Fraction {
  return { count: overview.activeThisWeek, total: overview.childCount }
}

export function childrenTouchingSkills(overview: SchoolOverview): Fraction {
  return { count: overview.childrenWithAnyEvents, total: overview.childCount }
}

/* ─────────────────────────────────────────────────── things to fix ── */

export type SchoolNote = 'NO_CHILDREN' | 'NOBODY_PLAYED' | 'CHILDREN_WITH_NO_CLASS' | 'NO_TEACHERS'

/**
 * The short list of things a head teacher should look at, in the order they
 * should look at them.
 *
 * 🔴 `CHILDREN_WITH_NO_CLASS` IS THE ONE THAT MUST NOT BE SILENT. `Player.classId`
 * is nullable, and org.prisma warns that such a child is *"invisible to every
 * scoped query in the product"* — including their own admin's. E1 is the last
 * screen where anyone could notice, so the count is a note rather than a
 * rounding difference between two tiles.
 *
 * ⚠️ NOT AN ALERT AND NOT A SCORE. An empty school on its first morning is not
 * unhealthy, so `NO_CHILDREN` comes first and suppresses the rest — otherwise
 * day one opens as three warnings about a school that has done nothing wrong.
 */
export function schoolNotes(overview: SchoolOverview): SchoolNote[] {
  if (overview.childCount === 0) return ['NO_CHILDREN']

  const notes: SchoolNote[] = []
  if (overview.childrenWithNoClass > 0) notes.push('CHILDREN_WITH_NO_CLASS')
  if (overview.activeThisWeek === 0) notes.push('NOBODY_PLAYED')
  if (overview.teacherCount === 0) notes.push('NO_TEACHERS')
  return notes
}

/* ──────────────────────────────────────────────── the class table ── */

export type ClassSort = 'name' | 'quietest' | 'largest'

export const CLASS_SORTS: readonly ClassSort[] = ['name', 'quietest', 'largest']

/**
 * 🔴 SORTS, NEVER FILTERS — the `rosterOrder.ts` and `consentRows.ts` rule, one
 * tier up. An admin working down a filtered list of "quiet classes" cannot see
 * which ones they have already chased, and a class that becomes active mid-week
 * vanishes from under them.
 *
 * ⚠️ ARCHIVED CLASSES SORT LAST IN EVERY ORDER, and they are still there. They
 * hold children and history (§8.2), and a school that cannot see last year's
 * class on this screen will conclude it was deleted.
 */
export function orderSchoolClasses(
  rows: readonly SchoolClassRow[],
  sort: ClassSort,
): SchoolClassRow[] {
  const byName = (a: SchoolClassRow, b: SchoolClassRow): number =>
    a.name.localeCompare(b.name, undefined, { numeric: true })

  return [...rows].sort((a, b) => {
    const archived = Number(a.archivedAt !== null) - Number(b.archivedAt !== null)
    if (archived !== 0) return archived

    if (sort === 'quietest') {
      /*
       * Quietest first, measured as children who did NOT play — an absolute
       * number, not a share. A class of 4 with nobody playing and a class of 24
       * with nobody playing are both "0 played", and the bigger one is the
       * bigger problem; a share would rank them identically.
       */
      const idleA = a.childCount - a.activeThisWeek
      const idleB = b.childCount - b.activeThisWeek
      if (idleA !== idleB) return idleB - idleA
    }
    if (sort === 'largest' && a.childCount !== b.childCount) {
      return b.childCount - a.childCount
    }
    return byName(a, b)
  })
}

/* ───────────────────────────────────────────────────── E5 coverage ── */

export interface CoverageSummary {
  covered: number
  /** 🔴 The denominator. Without it "9 covered" is a number, not a claim. */
  totalCodes: number
  uncovered: string[]
  /** Games with no events at all — where the gap actually is. */
  untouchedGames: string[]
  /**
   * 🔴 Whether C-3, physical development, is covered.
   *
   * `ncf.ts`: it *"is not in the table above and must still be reported"* — it
   * rides the motor core of three games rather than a category, and a report
   * that only summed the table would show the product's single biggest
   * differentiator as uncovered.
   */
  physicalDevelopmentCovered: boolean
}

/** Kept in step with `backend/src/modules/teaching/ncf.ts`, which owns it. */
export const PHYSICAL_DEVELOPMENT_CODE = 'C-3'

export function summariseCoverage(coverage: NcfCoverage): CoverageSummary {
  return {
    covered: coverage.codesCovered.length,
    totalCodes: coverage.codesCovered.length + coverage.codesUncovered.length,
    uncovered: coverage.codesUncovered,
    untouchedGames: coverage.rows.filter((r) => r.events === 0).map((r) => r.game),
    physicalDevelopmentCovered: coverage.codesCovered.includes(PHYSICAL_DEVELOPMENT_CODE),
  }
}

/**
 * The file E5 hands the admin, built in the browser from the JSON it already
 * fetched.
 *
 * ⚠️ CSV, NOT A PDF AND NOT A HOLISTIC PROGRESS CARD (OD-59). The HPC is a
 * specific Government of India artefact and no document in this repo defines its
 * fields; a guessed layout is a document a school might file. There is no PDF
 * renderer either — zero new dependencies.
 *
 * 🔴 THE SAME FORMULA-INJECTION ESCAPE THE SERVER USES, AND THE DUPLICATION IS
 * DELIBERATE RATHER THAN OVERLOOKED. `GET /school/coverage.csv` exists and is
 * authoritative, but `client.ts` calls `response.json()` on every body and a
 * plain `<a href>` cannot carry a bearer token — the limitation `coverageCsvPath`
 * already records for D7 and `downloadExport` already works around for P1. So
 * the browser builds the file from the JSON it fetched, with the escape copied
 * verbatim. A cell beginning `=`, `+`, `-` or `@` executes when the file is
 * opened in Excel, and these values are ours, which is exactly why it is easy to
 * forget. 📋 Reported: the real fix is a signed download URL.
 */
export function coverageToCsv(coverage: NcfCoverage): string {
  /*
   * 🔴 THE THIRD COPY OF THE ESCAPER, AND THE ONLY ONE THAT CANNOT BE REMOVED.
   *
   * §10 collapsed the backend's two into `backend/src/common/csv/csv-cell.ts`.
   * This one stays because the packages share generated *types*, not runtime
   * code — the browser cannot import from `backend/src`. **That file is the
   * source of truth; change one, change both.**
   *
   * ⚠️ And this output is a convenience, not the record: anything that leaves
   * the building should come from the server's `/school/coverage.csv`. The only
   * reason it exists is that `client.ts` calls `response.json()` on every body,
   * so a CSV endpoint cannot currently be fetched.
   */
  const esc = (v: string | number): string => {
    const s = String(v)
    const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe
  }
  const lines = [['Game', 'Category', 'NCF-FS codes', 'Events', 'Children'].map(esc).join(',')]
  for (const r of coverage.rows) {
    lines.push([r.game, r.category, r.codes.join(' '), r.events, r.children].map(esc).join(','))
  }
  return lines.join('\r\n')
}
